import {UserProvider} from "./contexts/user_context";
import React from 'react';
import './App.css';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import Home from './pages/Home';
import ErrorPage from './pages/ErrorPage';
import {EnhancedDocumentsProvider} from "./contexts/doc_contexts";
import {NavigationProvider} from "./contexts/NavContext/nav-context";
import {GroupProvider} from './contexts/group_context';
import {FolderProvider} from './contexts/folder_context';
import {NotificationProvider} from "./contexts/notification_context";
import {WatchListProvider} from "./contexts/watch_list_context";
import {JobProvider} from "./contexts/job_context";
import {TagProvider} from "./contexts/tag_context";
import {SavedSearchProvider} from "./contexts/saved_search_context";
import ErrorBoundary from "./contexts/error_context";
import LoginOrSignup from "./pages/LoginOrSignup";
import LandingPage from "./pages/LandingPage";
import LandingPageDrafting from "./pages/LandingPageDrafting";
import UserSettings from "./pages/UserSettings";
import MaintenancePage from "./pages/Maintenance";
import {MAINTENANCE_MODE} from "./feature_flags";

// Pick the landing page variant. Build-time env var wins (set per-Vercel-project);
// otherwise fall back to hostname so a single Vercel project with both domains also works.
const isDraftingVariant =
    process.env.REACT_APP_VARIANT === 'drafting'
    || (typeof window !== 'undefined' && window.location.hostname.startsWith('drafting.'));

const Landing = isDraftingVariant ? LandingPageDrafting : LandingPage;

declare global {
    interface Window {
        clickTimeout?: number;
    }
}

export enum AppView {
    LandingPage = "LANDING_PAGE",
    DocumentSearch = "DOCUMENT_SEARCH",
    MyDashboard = "MY_DASHBOARD",
    SingleDocumentView = "SINGLE_DOCUMENT_VIEW",
    FolderView = "FOLDER_VIEW",
    Login = "LOGIN_VIEW",
    SignUp = "SIGN_UP_VIEW",
    OAuthCallback = "OAUTH_CALLBACK_VIEW",
    CreateCase = "CREATE_CASE_VIEW",
    UserSettings = "USER_SETTINGS_VIEW",
}

export const AppViewToLinkMap: { [key in AppView]: string } = {
    [AppView.LandingPage]: "/",
    [AppView.DocumentSearch]: "/app/workspace/:groupId/:folderId",
    [AppView.MyDashboard]: "/app/",
    [AppView.SingleDocumentView]: "/app/doc/:groupId/:folderId/:docId",
    [AppView.FolderView]: "/app/folders/:groupId/",
    [AppView.Login]: "/app/login",
    [AppView.SignUp]: "/app/sign-up",
    [AppView.OAuthCallback]: "/app/oauth-callback",
    [AppView.CreateCase]: "/app/create-case",
    [AppView.UserSettings]: "/app/settings",
};

// Routes that require backend/auth - wrapped in all providers
const AuthenticatedRoutes: React.FC = () => {
    return (
        <UserProvider>
            <GroupProvider>
                <FolderProvider>
                    <EnhancedDocumentsProvider>
                        <JobProvider>
                            <TagProvider>
                                <SavedSearchProvider>
                                    <WatchListProvider>
                                        <NotificationProvider>
                                            <NavigationProvider>
                                                <Routes>
                                                    <Route path={AppViewToLinkMap[AppView.MyDashboard]}
                                                           element={<Home startingView={AppView.MyDashboard}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.FolderView]}
                                                           element={<Home startingView={AppView.FolderView}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.DocumentSearch]}
                                                           element={<Home startingView={AppView.DocumentSearch}/>}/>
                                                    <Route path="/app/workspace/:groupId/:folderId/:docId"
                                                           element={<Home startingView={AppView.DocumentSearch}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.SingleDocumentView]}
                                                           element={<Home startingView={AppView.SingleDocumentView}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.Login]}
                                                           element={<LoginOrSignup initialMode={'login'}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.SignUp]}
                                                           element={<LoginOrSignup initialMode={'login'}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.OAuthCallback]}
                                                           element={<LoginOrSignup initialMode={'callback'}/>}/>
                                                    <Route path={AppViewToLinkMap[AppView.UserSettings]}
                                                           element={<UserSettings/>}/>
                                                    <Route path="*" element={<ErrorPage/>}/>
                                                </Routes>
                                            </NavigationProvider>
                                        </NotificationProvider>
                                    </WatchListProvider>
                                </SavedSearchProvider>
                            </TagProvider>
                        </JobProvider>
                    </EnhancedDocumentsProvider>
                </FolderProvider>
            </GroupProvider>
        </UserProvider>
    );
};

const App: React.FC = () => {
    return (
        <div className="App bg-black font-sans">
            <BrowserRouter>
                <ErrorBoundary>
                    <Routes>
                        {/* Landing page - no backend required */}
                        <Route path={AppViewToLinkMap[AppView.LandingPage]} element={<Landing/>}/>
                        {/* All other routes require backend. In maintenance mode the backend
                            infra is paused, so render a static notice instead of mounting the
                            providers (which would fire API calls at a closed service). */}
                        <Route path="/*" element={MAINTENANCE_MODE ? <MaintenancePage/> : <AuthenticatedRoutes/>}/>
                    </Routes>
                </ErrorBoundary>
            </BrowserRouter>
        </div>
    );
};

export default App;
